/**
 * CENTRALIZED COLUMN DEFINITIONS
 * This file contains the default column configurations and field mappings for the application.
 */

// ============================================================================
// 1. CENTRALIZED FIELD KEYS & MAPPINGS
// ============================================================================
import type { InspectorColumnConfig } from '../components/inspector/types'
import type { ColumnConfig } from '../components/ColumnSelector'

export const FIELD_KEYS = {
    TIMESTAMP: ['timestamp', 'time', 'timestampIST'],
    // CE Fields
    CE_LTP: ['callPrice', 'ceLtp', 'ce_ltp', 'CE_LTP', 'lastPrice', 'cePrice', 'call_ltp', 'callClose', 'ceClose', 'CE Price', 'CE LTP', 'callLtp', 'ce_price', 'CE_Price', 'CE Close', 'CE_Close'],
    CE_OI: ['callOpenInterest', 'ceOI', 'ce_oi', 'CE_OI'],
    CE_VOL: ['callVolume', 'ceVol', 'ce_vol', 'CE_Volume'],
    CE_IV: ['callImpliedVolatility', 'ceIv', 'ce_iv', 'CE_IV'],
    CE_OI_CHG: ['callOpenInterestChange', 'ceOiChange', 'ce_oi_change', 'CE_OI_Change'],
    CE_PRICE_CHG: ['callChangePercent', 'ceChangePercent', 'ceChange', 'ce_change_p', 'change_p', 'pChange'],

    // PE Fields
    PE_LTP: ['putPrice', 'peLtp', 'pe_ltp', 'PE_LTP', 'lastPrice', 'pePrice', 'put_ltp', 'putClose', 'peClose', 'PE Price', 'PE LTP', 'putLtp', 'pe_price', 'PE_Price', 'PE Close', 'PE_Close'],
    PE_OI: ['putOpenInterest', 'peOI', 'pe_oi', 'PE_OI'],
    PE_VOL: ['putVolume', 'peVol', 'pe_vol', 'PE_Volume'],
    PE_IV: ['putImpliedVolatility', 'peIv', 'pe_iv', 'PE_IV'],
    PE_OI_CHG: ['putOpenInterestChange', 'peOiChange', 'pe_oi_change', 'PE_OI_Change'],
    PE_PRICE_CHG: ['putChangePercent', 'peChangePercent', 'peChange', 'pe_change_p', 'change_p', 'pChange'],

    // Greeks (CE)
    CE_DELTA: ['callDelta', 'ceDelta'],
    CE_THETA: ['callTheta', 'ceTheta'],
    CE_GAMMA: ['callGamma', 'ceGamma'],
    CE_VEGA: ['callVega', 'ceVega'],

    // Greeks (PE)
    PE_DELTA: ['putDelta', 'peDelta'],
    PE_THETA: ['putTheta', 'peTheta'],
    PE_GAMMA: ['putGamma', 'peGamma'],
    PE_VEGA: ['putVega', 'peVega'],

    // Absolute Change
    CE_CHANGE: ['callChange', 'ceChangeAbs', 'ce_change'],
    PE_CHANGE: ['putChange', 'peChangeAbs', 'pe_change'],

    // Bid/Ask (CE)
    CE_BID_QTY: ['callBidQty', 'ceBidQty'],
    CE_BID_PRICE: ['callBidPrice', 'ceBidPrice'],
    CE_ASK_PRICE: ['callAskPrice', 'ceAskPrice'],
    CE_ASK_QTY: ['callAskQty', 'ceAskQty'],

    // Bid/Ask (PE)
    PE_BID_QTY: ['putBidQty', 'peBidQty'],
    PE_BID_PRICE: ['putBidPrice', 'peBidPrice'],
    PE_ASK_PRICE: ['putAskPrice', 'peAskPrice'],
    PE_ASK_QTY: ['putAskQty', 'peAskQty'],

    // Base Fields
    STRIKE: ['strikePrice', 'strike', 'strike_price'],
    EXPIRY: ['expiryDate', 'expiry'],

    // Strategy Fields
    STRATEGY_NAME: ['strategy', 'name', 'strategyName', 'id'],
    STRATEGY_TYPE: ['type', 'strategyType'],
    NET_PREMIUM: ['netPremium', 'totalPremium', 'net_premium'],
    MAX_PROFIT: ['maxProfit', 'max_profit'],
    MAX_LOSS: ['maxLoss', 'max_loss'],
    BREAKEVEN_LOWER: ['breakevenLower', 'be_lower', 'lower_be'],
    BREAKEVEN_UPPER: ['breakevenUpper', 'be_upper', 'upper_be'],
    POP: ['pop', 'probOfProfit'],
    RISK_REWARD: ['riskReward', 'rr', 'risk_reward'],

    // Analysis
    RESISTANCE: ['resistanceStrike', 'resistance_strike', 'callResistance'],
    RESISTANCE_2: ['resistanceStrike2', 'resistance_strike_2', 'resistance_strike2'],
    SUPPORT: ['supportStrike', 'support_strike', 'putSupport'],
    SUPPORT_2: ['supportStrike2', 'support_strike_2', 'support_strike2'],

    // Counts (Re-added)
    CE_COUNT: ['ceCount', 'ce_count'],
    PE_COUNT: ['peCount', 'pe_count'],
    FINAL_COUNT: ['finalCount', 'totalCount', 'total_count', 'final_count'],
    ATM: ['atm', 'atmStrike', 'atm_strike'],

    // Metrics
    VARIANCE: ['finalVariance', 'variance', 'oiVariance'],
    SHIFT: ['shift', 'shiftValue'],
    SHIFT_OLD: ['shiftOld', 'shift_old'],

    // Dashboard/Variance Extra
    MAX_OI_DIFF: ['maxDiff', 'max_diff', 'maxOiDiff'],
    MIN_OI_DIFF: ['minDiff', 'min_diff', 'minOiDiff'],
    MAX_STRIKE: ['maxStrike', 'max_strike'],
    MIN_STRIKE: ['minStrike', 'min_strike'],
    SPOT_PRICE: ['spotPrice', 'spot_price', 'underlyingPrice', 'lastPrice'],
    ANALYSIS: ['analysis', 'analysis_mode'],
    RESISTANCE_VALUE: ['resistanceValue', 'resistance_value'],
    SUPPORT_VALUE: ['supportValue', 'support_value'],
    CE_OTM: ['ceOtmStrike', 'ce_otm_strike', 'ceOtm', 'ce_otm'],
    PE_OTM: ['peOtmStrike', 'pe_otm_strike', 'peOtm', 'pe_otm'],

    // Option Premium Profile (3-Day Parquet + Intraday + Live S/R & POC)
    CE_SUPPORT_PREM: ['ceSupportPrem', 'ce_support_prem'],
    CE_RES_PREM: ['ceResPrem', 'ce_res_prem'],
    CE_POC_PREM: ['cePocPrem', 'ce_poc_prem'],
    PE_SUPPORT_PREM: ['peSupportPrem', 'pe_support_prem'],
    PE_RES_PREM: ['peResPrem', 'pe_res_prem'],
    PE_POC_PREM: ['pePocPrem', 'pe_poc_prem'],
} as const

/**
 * Helper to get first valid value from an object using FIELD_KEYS
 */
export function getFieldValue(obj: any, keys: readonly string[]): any {
    if (!obj) return undefined
    for (const key of keys) {
        if (obj[key] !== undefined) return obj[key]
    }
    return undefined
}

// ============================================================================
// 2. COLUMN DEFINITIONS (UI CONSTANTS)
// ============================================================================
export const COLUMN_IDS = {
    TIME: 'time',
    RANK: 'rank',

    CE_LTP: 'ce_ltp',
    PE_LTP: 'pe_ltp',

    RESISTANCE_VALUE: 'resistance_value',
    SUPPORT_VALUE: 'support_value',

    RES_STRIKE: 'resistance_strike',
    RES_STRIKE_2: 'resistance_strike2',
    SUP_STRIKE: 'support_strike',
    SUP_STRIKE_2: 'support_strike2',

    CE_SUPPORT_PREM: 'ce_support_prem',
    CE_RES_PREM: 'ce_res_prem',
    CE_POC_PREM: 'ce_poc_prem',
    PE_SUPPORT_PREM: 'pe_support_prem',
    PE_RES_PREM: 'pe_res_prem',
    PE_POC_PREM: 'pe_poc_prem',

    CE_COUNT: 'ce_count',
    PE_COUNT: 'pe_count',
    FINAL_COUNT: 'final_count',

    MAX_OI_DIFF: 'max_oi_diff',
    MIN_OI_DIFF: 'min_oi_diff',
    MAX_STRIKE: 'max_strike',
    MIN_STRIKE: 'min_strike',
    VARIANCE: 'variance',

    SHIFT: 'shift',
    SHIFT_OLD: 'shift_old',
    ATM: 'atm_strike',
    CE_OTM: 'ce_otm',
    PE_OTM: 'pe_otm',
    COMBINED_OI: 'combined_oi',
    COMBINED_OI_CHANGE: 'combined_oi_change',
    COMBINED_OI_CHANGE_P: 'combined_oi_change_p',
    COMBINED_VOL: 'combined_volume',
    COMBINED_VOL_CHANGE: 'combined_vol_change',
    COMBINED_VOL_CHANGE_P: 'combined_vol_change_p',
} as const

// ============================================================================
// 3. TABLE SPECIFIC CONFIGURATIONS
// ============================================================================

export const CROSSOVER_COLUMNS = [
    { id: COLUMN_IDS.TIME, label: 'Time', visible: true },
    { id: COLUMN_IDS.RANK, label: 'Rank', visible: true },
    { id: COLUMN_IDS.CE_LTP, label: 'CE LTP', visible: true },
    { id: COLUMN_IDS.RESISTANCE_VALUE, label: 'OI/Volume', visible: true },
    { id: COLUMN_IDS.RES_STRIKE, label: 'Resistance', visible: true },
    { id: COLUMN_IDS.RES_STRIKE_2, label: 'Resistance 2', visible: true },
    { id: COLUMN_IDS.SUP_STRIKE, label: 'Support', visible: true },
    { id: COLUMN_IDS.SUP_STRIKE_2, label: 'Support 2', visible: true },
    { id: COLUMN_IDS.SUPPORT_VALUE, label: 'OI/Volume', visible: true },
    { id: COLUMN_IDS.PE_LTP, label: 'PE LTP', visible: true },
    { id: COLUMN_IDS.CE_COUNT, label: 'CE Count', visible: true },
    { id: COLUMN_IDS.PE_COUNT, label: 'PE Count', visible: true },
    { id: COLUMN_IDS.FINAL_COUNT, label: 'Final Count', visible: true },
    { id: COLUMN_IDS.MAX_OI_DIFF, label: 'Max OI % Diff', visible: true },
    { id: COLUMN_IDS.MIN_OI_DIFF, label: 'Min OI % Diff', visible: true },
    { id: COLUMN_IDS.VARIANCE, label: 'OI% Variance', visible: true },
    { id: COLUMN_IDS.SHIFT, label: 'Shift', visible: true },
    { id: COLUMN_IDS.SHIFT_OLD, label: 'Shift Old', visible: true },
    { id: COLUMN_IDS.ATM, label: 'ATM Strike', visible: true },
    { id: COLUMN_IDS.CE_OTM, label: 'CE OTM Strike', visible: true },
    { id: COLUMN_IDS.PE_OTM, label: 'PE OTM Strike', visible: true },
]

export const VARIANCE_COLUMNS = [
    { id: COLUMN_IDS.TIME, label: 'Time', visible: true },
    { id: COLUMN_IDS.MAX_OI_DIFF, label: 'Max OI', visible: true },
    { id: COLUMN_IDS.MAX_STRIKE, label: 'MAX STRIKE', visible: true },
    { id: COLUMN_IDS.MIN_STRIKE, label: 'MIN STRIKE', visible: true },
    { id: COLUMN_IDS.MIN_OI_DIFF, label: 'Min OI', visible: true },
    { id: COLUMN_IDS.CE_COUNT, label: 'CE Count', visible: true },
    { id: COLUMN_IDS.PE_COUNT, label: 'PE Count', visible: true },
    { id: COLUMN_IDS.FINAL_COUNT, label: 'Final Count', visible: true },
    { id: COLUMN_IDS.VARIANCE, label: 'OI% Variance', visible: true },
    { id: COLUMN_IDS.SHIFT, label: 'Shift', visible: true },
    { id: COLUMN_IDS.SHIFT_OLD, label: 'Shift Old', visible: true },
    { id: 'signal', label: 'Signal', visible: true },
    { id: COLUMN_IDS.ATM, label: 'ATM', visible: true },
    { id: COLUMN_IDS.CE_OTM, label: 'CE OTM Strike', visible: true },
    { id: COLUMN_IDS.PE_OTM, label: 'PE OTM Strike', visible: true },
]

export const DASHBOARD_HISTORY_COLUMNS = [
    { id: COLUMN_IDS.TIME, label: 'Time Log', visible: true },
    { id: COLUMN_IDS.SHIFT, label: 'Shift', visible: true },
    { id: 'res_strike', label: 'Resistance', visible: true },
    { id: 'res_ltp', label: 'R1 LTP', visible: true },
    { id: 'res2_strike', label: 'Resistance 2', visible: true },
    { id: 'res2_ltp', label: 'R2 LTP', visible: true },
    { id: 'sup_strike', label: 'Support', visible: true },
    { id: 'sup_ltp', label: 'Sup LTP', visible: true },
]

export const INSPECTOR_COLUMNS = [
    {
        id: 'iv',
        label: 'IV',
        ceField: FIELD_KEYS.CE_IV,
        peField: FIELD_KEYS.PE_IV,
        format: 'decimal',
        visible: true,
    },
    {
        id: 'oi_change',
        label: 'OI Chg (Lakh)',
        ceField: FIELD_KEYS.CE_OI_CHG,
        peField: FIELD_KEYS.PE_OI_CHG,
        format: 'lakh',
        visible: true,
    },
    {
        id: 'change_p',
        label: 'Price Chg %',
        ceField: FIELD_KEYS.CE_PRICE_CHG,
        peField: FIELD_KEYS.PE_PRICE_CHG,
        format: 'percent',
        visible: true,
    },
    {
        id: 'ltp',
        label: 'Price',
        ceField: FIELD_KEYS.CE_LTP,
        peField: FIELD_KEYS.PE_LTP,
        format: 'decimal',
        visible: true,
    },
    {
        id: 'oi',
        label: 'OI (Lakh)',
        ceField: FIELD_KEYS.CE_OI,
        peField: FIELD_KEYS.PE_OI,
        format: 'lakh',
        visible: true,
    },
    {
        id: 'volume',
        label: 'Volume (Cr)',
        ceField: FIELD_KEYS.CE_VOL,
        peField: FIELD_KEYS.PE_VOL,
        format: 'crore',
        visible: true,
    },
    {
        id: 'cum_price',
        label: 'Total Prem',
        ceField: FIELD_KEYS.CE_LTP,
        peField: FIELD_KEYS.PE_LTP,
        format: 'cumulative',
        visible: true,
    },
    // Combined Metrics (Same Strike)
    {
        id: 'ce_combined_oi',
        label: 'CE Comb OI',
        ceField: ['ceCombinedOI'],
        format: 'lakh',
        visible: true,
    },
    {
        id: 'ce_combined_vol',
        label: 'CE Comb Vol',
        ceField: ['ceCombinedVol'],
        format: 'crore',
        visible: true,
    },
    {
        id: 'pe_combined_oi',
        label: 'PE Comb OI',
        peField: ['peCombinedOI'],
        format: 'lakh',
        visible: true,
    },
    {
        id: 'pe_combined_vol',
        label: 'PE Comb Vol',
        peField: ['peCombinedVol'],
        format: 'crore',
        visible: true,
    },

    // Cross-Strike Differences
    {
        id: 'oi_diff',
        label: 'OI Diff (Lakh)',
        ceField: ['crossOIDiff'],
        peField: ['crossOIDiff'],
        format: 'lakh',
        visible: true,
    },
    {
        id: 'vol_diff',
        label: 'Vol Diff (Cr)',
        ceField: ['crossVolDiff'],
        peField: ['crossVolDiff'],
        format: 'crore',
        visible: true,
    },
] as InspectorColumnConfig[]

export const DASHBOARD_RANKING_COLUMNS = [
    { id: 'oi', label: 'OI (Lakhs)', visible: true },
    { id: 'volume', label: 'Volume (Cr)', visible: true },
    { id: 'iv', label: 'IV', visible: true },
    { id: 'ltp', label: 'LTP', visible: true },
    { id: 'change_p', label: 'Price Chg %', visible: true },
]

export const MULTI_STRATEGY_COLUMNS = [
    { id: 'strategy', label: 'Strategy', visible: true },
    { id: 'net_premium', label: 'Net Premium', visible: true },
    { id: 'max_profit', label: 'Max Profit', visible: true },
    { id: 'max_loss', label: 'Max Loss', visible: true },
    { id: 'breakeven', label: 'Breakeven Range', visible: true },
    { id: 'pop', label: 'PoP %', visible: true },
    { id: 'rr', label: 'R:R', visible: true },
    { id: 'action', label: 'Action', visible: true },
]

export const OPTION_CHAIN_COLUMNS: ColumnConfig[] = [
    { id: 'oi', label: 'OI (Lakhs)', visible: true },
    { id: 'oi_change', label: 'Chng in OI (Lakhs)', visible: true },
    { id: 'volume', label: 'Volume', visible: true },
    { id: 'iv', label: 'IV %', visible: true },
    { id: 'ltp', label: 'LTP', visible: true },
    { id: 'ce_support_prem', label: 'CE Support', visible: true },
    { id: 'ce_poc_prem', label: 'CE POC', visible: true },
    { id: 'ce_res_prem', label: 'CE Resist', visible: true },
    { id: 'change_p', label: 'Chng %', visible: true },
    { id: 'vega', label: 'Vega', visible: false },
    { id: 'theta', label: 'Theta', visible: false },
    { id: 'gamma', label: 'Gamma', visible: false },
    { id: 'delta', label: 'Delta', visible: false },
    { id: 'combined_oi', label: 'Combined OI', visible: true },
    { id: 'combined_oi_change', label: 'Combined OI Chg', visible: true },
    { id: 'combined_oi_change_p', label: 'Combined OI Chg %', visible: true },
    { id: 'combined_volume', label: 'Combined Volume', visible: true },
    { id: 'combined_vol_change', label: 'Combined Vol Chg', visible: true },
    { id: 'combined_vol_change_p', label: 'Combined Vol Chg %', visible: true },
    { id: 'pe_support_prem', label: 'PE Support', visible: true },
    { id: 'pe_poc_prem', label: 'PE POC', visible: true },
    { id: 'pe_res_prem', label: 'PE Resist', visible: true },
    { id: 'vol_diff', label: 'Vol Diff (CE - PE)', visible: true },
    { id: 'oi_diff', label: 'OI Diff (PE - CE)', visible: true },
    { id: 'vol_oi', label: 'Vol-OI', visible: true },
    { id: 'vol_oi_chng', label: 'Vol-OI-Chng', visible: true },
]

export const ITM_OTM_HISTORY_COLUMNS: ColumnConfig[] = [
    { id: 'time', label: 'Time', visible: true },
    { id: 'v1strike', label: 'V1 Strike', visible: true },
    { id: 'itm_call', label: 'ITM Call', visible: true },
    { id: 'itm_put', label: 'ITM Put', visible: true },
    { id: 'itm_net', label: 'ITM Net & %', visible: true },
    { id: 'otm_call', label: 'OTM Call', visible: true },
    { id: 'otm_put', label: 'OTM Put', visible: true },
    { id: 'otm_net', label: 'OTM Net & %', visible: true },
    { id: 'final_net', label: 'Final Net & %', visible: true },
    { id: 'index_ltp', label: 'Index LTP', visible: true },
]

export const ABOVE_BELOW_HISTORY_COLUMNS: ColumnConfig[] = [
    { id: 'time', label: 'Time', visible: true },
    { id: 'atm_strike', label: 'ATM Strike', visible: true },
    { id: 'index_ltp', label: 'Index LTP', visible: true },
    { id: 'below_comb_oi', label: 'Below Comb OI', visible: true },
    { id: 'above_comb_oi', label: 'Above Comb OI', visible: true },
    { id: 'net_comb_oi', label: 'Net OI & % (B - A)', visible: true },
    { id: 'below_comb_vol', label: 'Below Comb Vol', visible: true },
    { id: 'above_comb_vol', label: 'Above Comb Vol', visible: true },
    { id: 'net_comb_vol', label: 'Net Vol & % (B - A)', visible: true },
    { id: 'below_comb_oi_chg', label: 'Below Comb OI Chg', visible: true },
    { id: 'above_comb_oi_chg', label: 'Above Comb OI Chg', visible: true },
    { id: 'net_comb_oi_chg', label: 'Net OI Chg & % (B - A)', visible: true },
]

export const INTRADAY_HISTORY_COLUMNS: ColumnConfig[] = [
    { id: 'time', label: 'Time', visible: true },
    { id: 'ce_total_oi', label: 'CE Total OI', visible: true },
    { id: 'pe_total_oi', label: 'PE Total OI', visible: true },
    { id: 'ce_total_change_oi', label: 'CE Total Change OI', visible: true },
    { id: 'pe_total_change_oi', label: 'PE Total Change OI', visible: true },
    { id: 'ce_total_volume', label: 'CE Total Volume', visible: true },
    { id: 'pe_total_volume', label: 'PE Total Volume', visible: true },
    { id: 'total_combined_oi', label: 'Total Combined OI', visible: true },
    { id: 'total_combined_volume', label: 'Total Combined Volume', visible: true },
    { id: 'total_comb_oi_chg', label: 'Total Comb OI Chg', visible: true },
    { id: 'total_comb_oi_chg_p', label: 'Total Comb OI Chg%', visible: true },
    { id: 'total_vol_diff', label: 'Total Vol Diff & %', visible: true },
    { id: 'total_oi_diff', label: 'Total OI Diff & %', visible: true },
    { id: 'ce_total_vol_oi', label: 'CE Total Vol-OI', visible: true },
    { id: 'pe_total_vol_oi', label: 'PE Total Vol-OI', visible: true },
    { id: 'ce_total_vol_oi_chng', label: 'CE Total Vol-OI-Chng', visible: true },
    { id: 'pe_total_vol_oi_chng', label: 'PE Total Vol-OI-Chng', visible: true },
    { id: 'index_ltp', label: 'Index LTP', visible: true },
]

export const MARKET_WATCH_COLUMNS: ColumnConfig[] = [
    { id: 'strike', label: 'Strike', visible: true },
    { id: 'type', label: 'Type', visible: true },
    { id: 'itmOtm', label: 'ITM/OTM', visible: true },
    { id: 'stVar', label: 'St Var', visible: true },
    { id: 'rankDelta', label: 'Rank Δ', visible: true },
    { id: 'surge', label: 'Surge', visible: true },
    { id: 'momentum', label: 'Momentum', visible: true },
    { id: 'flow', label: 'Flow %', visible: true },
    { id: 'vpcr', label: 'Vol PCR', visible: true },
    { id: 'awayLtp', label: 'Away LTP', visible: true },
    { id: 'ltp', label: 'LTP', visible: true },
    { id: 'supportPrem', label: 'Supp Floor', visible: true },
    { id: 'pocPrem', label: 'POC (3-Day)', visible: true },
    { id: 'resPrem', label: 'Resist Ceil', visible: true },
    { id: 'chgP', label: 'Chg%', visible: true },
    { id: 'oi', label: 'OI', visible: true },
    { id: 'volume', label: 'Volume', visible: true },
    { id: 'volDiff', label: 'Vol Diff', visible: true },
    { id: 'oiChg', label: 'OI Chg', visible: true },
    { id: 'volOiDiff', label: 'Vol-OI', visible: true },
    { id: 'volOiChg', label: 'Vol-OI-Chng', visible: true },
    { id: 'iv', label: 'IV', visible: true },
    { id: 'combOI', label: 'Comb OI', visible: true },
    { id: 'combVol', label: 'Comb Vol', visible: true },
    { id: 'breakeven', label: 'B/E Dist', visible: true },
    { id: 'signal', label: 'Signal', visible: true },
    { id: 'rvol', label: 'RVOL Breakout', visible: true },
    { id: 'baseChg', label: 'Base Chng', visible: true },
    { id: 'baseChgP', label: 'Base Chng%', visible: true },
]

export const SPLIT_MARKET_WATCH_COLUMNS: ColumnConfig[] = [
    { id: 'strike', label: 'Strike', visible: true },
    { id: 'type', label: 'Type', visible: true },
    { id: 'itmOtm', label: 'ITM/OTM', visible: false },
    { id: 'stVar', label: 'St Var', visible: false },
    { id: 'rankDelta', label: 'Rank Δ', visible: true },
    { id: 'surge', label: 'Surge', visible: false },
    { id: 'momentum', label: 'Momentum', visible: false },
    { id: 'flow', label: 'Flow %', visible: false },
    { id: 'vpcr', label: 'Vol PCR', visible: false },
    { id: 'awayLtp', label: 'Away LTP', visible: false },
    { id: 'ltp', label: 'LTP', visible: true },
    { id: 'supportPrem', label: 'Supp Floor', visible: true },
    { id: 'pocPrem', label: 'POC (3-Day)', visible: true },
    { id: 'resPrem', label: 'Resist Ceil', visible: true },
    { id: 'chgP', label: 'Chg%', visible: true },
    { id: 'oi', label: 'OI', visible: true },
    { id: 'volume', label: 'Volume', visible: true },
    { id: 'volDiff', label: 'Vol Diff', visible: true },
    { id: 'oiChg', label: 'OI Chg', visible: true },
    { id: 'volOiDiff', label: 'Vol-OI', visible: true },
    { id: 'volOiChg', label: 'Vol-OI-Chng', visible: true },
    { id: 'iv', label: 'IV', visible: false },
    { id: 'combOI', label: 'Comb OI', visible: true },
    { id: 'combVol', label: 'Comb Vol', visible: true },
    { id: 'breakeven', label: 'B/E Dist', visible: false },
    { id: 'signal', label: 'Signal', visible: false },
    { id: 'rvol', label: 'RVOL Breakout', visible: false },
    { id: 'baseChg', label: 'Base Chng', visible: false },
    { id: 'baseChgP', label: 'Base Chng%', visible: false },
]

export const CONFIG_COLUMNS = {
    CROSSOVER: CROSSOVER_COLUMNS,
    VARIANCE: VARIANCE_COLUMNS,
    DASHBOARD: DASHBOARD_HISTORY_COLUMNS,
    INSPECTOR: INSPECTOR_COLUMNS,
    DASHBOARD_RANKING: DASHBOARD_RANKING_COLUMNS,
    MULTI_STRATEGY: MULTI_STRATEGY_COLUMNS,
    OPTION_CHAIN: OPTION_CHAIN_COLUMNS,
    INTRADAY_HISTORY: INTRADAY_HISTORY_COLUMNS,
    ABOVE_BELOW_HISTORY: ABOVE_BELOW_HISTORY_COLUMNS,
    ITM_OTM_HISTORY: ITM_OTM_HISTORY_COLUMNS,
    MARKET_WATCH: MARKET_WATCH_COLUMNS,
    SPLIT_MARKET_WATCH: SPLIT_MARKET_WATCH_COLUMNS,
}

// --- HELPER EXPORTS ---
export const getInspectorColumns = () => INSPECTOR_COLUMNS
export const getColumnLabel = (id: string, columns: any[]) => columns.find((c) => c.id === id)?.label || id
